if (!customElements.get('collection-infinite-scroll')) {
  customElements.define(
    'collection-infinite-scroll',
    class CollectionInfiniteScroll extends HTMLElement {
      connectedCallback() {
        this.grid = this.querySelector('#product-grid');
        this.sentinel = this.querySelector('[data-infinite-scroll-sentinel]');
        this.pagination = this.querySelector('.pagination-wrapper');
        this.status = this.querySelector('[data-infinite-scroll-status]');
        this.sectionId = this.dataset.sectionId || this.grid?.dataset.id;
        this.nextUrl = this.dataset.nextUrl || this.readNextUrl(this);
        this.isLoading = false;
        this.abortController = null;
        this.observer = null;
        this.requestedUrl = null;
        this.secondaryImagePointer = null;
        this.suppressedProductCard = null;
        this.suppressedProductCardTimeout = null;
        this.boundProductCardWindowListeners = false;

        this.onProductCardPointerDown = this.onProductCardPointerDown.bind(this);
        this.onProductCardPointerMove = this.onProductCardPointerMove.bind(this);
        this.onProductCardPointerUp = this.onProductCardPointerUp.bind(this);
        this.onProductCardPointerCancel = this.onProductCardPointerCancel.bind(this);
        this.onProductCardClick = this.onProductCardClick.bind(this);
        this.resetProductCardSecondaryImages = this.resetProductCardSecondaryImages.bind(this);

        this.initializeProductCardSecondaryImages();

        if (!this.grid || !this.sentinel || !this.sectionId || !this.nextUrl || !('IntersectionObserver' in window)) {
          return;
        }

        this.classList.add('is-enhanced');
        this.observer = new IntersectionObserver(this.onIntersection.bind(this), {
          rootMargin: '800px 0px',
        });
        this.observer.observe(this.sentinel);
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.abortController?.abort();
        this.destroyProductCardSecondaryImages();
        this.observer = null;
        this.abortController = null;
        this.isLoading = false;
        this.requestedUrl = null;
      }

      onIntersection(entries) {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        this.loadNextPage();
      }

      async loadNextPage() {
        if (this.isLoading || !this.nextUrl) return;

        const requestUrl = this.buildSectionUrl(this.nextUrl);
        if (!requestUrl || requestUrl === this.requestedUrl) return;

        this.isLoading = true;
        this.requestedUrl = requestUrl;
        this.abortController = new AbortController();
        this.classList.add('is-loading');

        try {
          const response = await fetch(requestUrl, { signal: this.abortController.signal });
          if (!response.ok) throw new Error(`Collection infinite scroll request failed with status ${response.status}.`);

          const responseText = await response.text();
          const parsedHTML = new DOMParser().parseFromString(responseText, 'text/html');
          const returnedContainer = parsedHTML.getElementById('ProductGridContainer');
          const returnedGrid = parsedHTML.getElementById('product-grid');
          if (!returnedContainer || !returnedGrid) throw new Error('Collection infinite scroll response is missing grid markup.');
          if (!this.isConnected || this.abortController.signal.aborted) return;

          const nextUrl = this.readNextUrl(parsedHTML);
          const appendedItems = this.appendGridItems(returnedGrid);
          this.updatePagination(parsedHTML);
          this.nextUrl = nextUrl;

          if (appendedItems.length && typeof initializeScrollAnimationTrigger === 'function') {
            initializeScrollAnimationTrigger({
              getElementsByClassName: (className) => appendedItems.filter((item) => item.classList.contains(className)),
            });
          }

          if (!this.nextUrl) {
            this.finish();
          }
        } catch (error) {
          if (error?.name !== 'AbortError') this.fail();
        } finally {
          if (!this.isConnected) return;
          this.classList.remove('is-loading');
          this.isLoading = false;
          this.abortController = null;
          this.requestedUrl = null;
        }
      }

      buildSectionUrl(nextUrl) {
        try {
          const url = new URL(nextUrl, window.location.origin);
          url.searchParams.set('section_id', this.sectionId);
          return url.toString();
        } catch (error) {
          return null;
        }
      }

      appendGridItems(returnedGrid) {
        const existingProductIds = new Set(
          Array.from(this.grid.querySelectorAll(':scope > li[data-product-id]'))
            .map((item) => item.dataset.productId)
            .filter(Boolean)
        );
        const fragment = document.createDocumentFragment();
        const appendedItems = [];

        returnedGrid.querySelectorAll(':scope > li.grid__item').forEach((item) => {
          const productId = item.dataset.productId;
          if (productId && existingProductIds.has(productId)) return;
          if (productId) existingProductIds.add(productId);

          appendedItems.push(item);
          fragment.appendChild(item);
        });

        this.grid.appendChild(fragment);
        return appendedItems;
      }

      updatePagination(parsedHTML) {
        const returnedPagination = parsedHTML.querySelector('.pagination-wrapper');

        if (returnedPagination && this.pagination) {
          this.pagination.replaceWith(returnedPagination);
          this.pagination = returnedPagination;
        } else if (returnedPagination) {
          this.querySelector('.collection')?.appendChild(returnedPagination);
          this.pagination = returnedPagination;
        } else if (this.pagination) {
          this.pagination.remove();
          this.pagination = null;
        }
      }

      readNextUrl(root) {
        return root.querySelector('[data-pagination-next]')?.getAttribute('href') || null;
      }

      finish() {
        this.nextUrl = null;
        this.observer?.disconnect();
        this.observer = null;
        this.sentinel?.setAttribute('hidden', '');
      }

      fail() {
        this.nextUrl = null;
        this.observer?.disconnect();
        this.observer = null;
        this.classList.remove('is-enhanced');
        this.sentinel?.setAttribute('hidden', '');
      }

      initializeProductCardSecondaryImages() {
        if (!this.grid || !('PointerEvent' in window)) return;

        this.grid.addEventListener('pointerdown', this.onProductCardPointerDown);
        this.grid.addEventListener('pointermove', this.onProductCardPointerMove, { passive: true });
        this.grid.addEventListener('click', this.onProductCardClick, true);
        window.addEventListener('pagehide', this.resetProductCardSecondaryImages);
        window.addEventListener('pageshow', this.resetProductCardSecondaryImages);
        window.addEventListener('blur', this.resetProductCardSecondaryImages);
      }

      destroyProductCardSecondaryImages() {
        this.grid?.removeEventListener('pointerdown', this.onProductCardPointerDown);
        this.grid?.removeEventListener('pointermove', this.onProductCardPointerMove);
        this.grid?.removeEventListener('click', this.onProductCardClick, true);
        window.removeEventListener('pagehide', this.resetProductCardSecondaryImages);
        window.removeEventListener('pageshow', this.resetProductCardSecondaryImages);
        window.removeEventListener('blur', this.resetProductCardSecondaryImages);
        this.removeProductCardWindowListeners();
        this.resetProductCardSecondaryImages();
      }

      onProductCardPointerDown(event) {
        if (event.pointerType !== 'touch' || (event.button != null && event.button !== 0)) return;

        const card = event.target.closest('.product-card-wrapper');
        if (!card || !this.grid.contains(card)) return;

        const cardMedia = card.querySelector('.card__media');
        const hoverMedia = card.querySelector('.media.media--hover-effect');
        if (!cardMedia || !hoverMedia || !hoverMedia.querySelector('img + img')) return;

        const mediaBounds = cardMedia.getBoundingClientRect();
        if (
          event.clientX < mediaBounds.left ||
          event.clientX > mediaBounds.right ||
          event.clientY < mediaBounds.top ||
          event.clientY > mediaBounds.bottom
        ) {
          return;
        }

        this.clearSuppressedProductCard();
        this.activateProductCardSecondaryImage(card);
        this.secondaryImagePointer = {
          card,
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          dragged: false,
        };
        this.addProductCardWindowListeners();
      }

      onProductCardPointerMove(event) {
        const pointerState = this.secondaryImagePointer;
        if (!pointerState || pointerState.pointerId !== event.pointerId) return;

        const moveX = Math.abs(event.clientX - pointerState.startX);
        const moveY = Math.abs(event.clientY - pointerState.startY);
        if (moveX <= 8 && moveY <= 8) return;

        pointerState.dragged = true;
      }

      onProductCardPointerUp(event) {
        const pointerState = this.secondaryImagePointer;
        if (!pointerState || pointerState.pointerId !== event.pointerId) return;

        if (pointerState.dragged) this.suppressProductCardClick(pointerState.card);
        this.secondaryImagePointer = null;
        this.removeProductCardWindowListeners();
      }

      onProductCardPointerCancel(event) {
        const pointerState = this.secondaryImagePointer;
        if (!pointerState || pointerState.pointerId !== event.pointerId) return;

        this.suppressProductCardClick(pointerState.card);
        this.secondaryImagePointer = null;
        this.removeProductCardWindowListeners();
      }

      onProductCardClick(event) {
        if (!this.suppressedProductCard || !this.suppressedProductCard.contains(event.target)) return;

        event.preventDefault();
        event.stopPropagation();
        this.clearSuppressedProductCard();
      }

      addProductCardWindowListeners() {
        if (this.boundProductCardWindowListeners) return;

        window.addEventListener('pointerup', this.onProductCardPointerUp, { passive: true });
        window.addEventListener('pointercancel', this.onProductCardPointerCancel, { passive: true });
        this.boundProductCardWindowListeners = true;
      }

      removeProductCardWindowListeners() {
        if (!this.boundProductCardWindowListeners) return;

        window.removeEventListener('pointerup', this.onProductCardPointerUp);
        window.removeEventListener('pointercancel', this.onProductCardPointerCancel);
        this.boundProductCardWindowListeners = false;
      }

      activateProductCardSecondaryImage(activeCard) {
        this.querySelectorAll('.product-card-wrapper.is-secondary-image-active').forEach((card) => {
          if (card !== activeCard) card.classList.remove('is-secondary-image-active');
        });
        activeCard.classList.add('is-secondary-image-active');
      }

      suppressProductCardClick(card) {
        this.suppressedProductCard = card;
        clearTimeout(this.suppressedProductCardTimeout);
        this.suppressedProductCardTimeout = setTimeout(() => this.clearSuppressedProductCard(), 500);
      }

      clearSuppressedProductCard() {
        this.suppressedProductCard = null;
        clearTimeout(this.suppressedProductCardTimeout);
        this.suppressedProductCardTimeout = null;
      }

      resetProductCardSecondaryImages() {
        this.secondaryImagePointer?.card?.classList.remove('is-secondary-image-active');
        this.querySelectorAll('.product-card-wrapper.is-secondary-image-active').forEach((card) => {
          card.classList.remove('is-secondary-image-active');
        });
        this.secondaryImagePointer = null;
        this.removeProductCardWindowListeners();
        this.clearSuppressedProductCard();
      }
    }
  );
}

(() => {
  const STORAGE_PREFIX = 'sylavieCollectionScroll:';
  const MIN_RESTORE_Y = 100;
  const TOP_THRESHOLD = 100;
  const RESTORED_THRESHOLD = 120;

  if (!document.getElementById('product-grid')) return;

  const storageKey = `${STORAGE_PREFIX}${window.location.pathname}${window.location.search}`;

  function readSavedScrollY() {
    try {
      return Number(window.sessionStorage.getItem(storageKey) || 0);
    } catch (error) {
      return 0;
    }
  }

  function writeSavedScrollY() {
    const scrollY = window.scrollY || window.pageYOffset;
    if (scrollY <= MIN_RESTORE_Y) return;

    try {
      window.sessionStorage.setItem(storageKey, String(Math.round(scrollY)));
    } catch (error) {
      // Ignore unavailable storage; native restoration will still apply where supported.
    }
  }

  function restoreIfSafariMissedBFCacheScroll() {
    const savedScrollY = readSavedScrollY();
    if (savedScrollY <= MIN_RESTORE_Y) return;
    if (Math.abs(window.scrollY - savedScrollY) <= RESTORED_THRESHOLD) return;
    if (window.scrollY > TOP_THRESHOLD) return;

    window.scrollTo({ top: savedScrollY, left: window.scrollX, behavior: 'auto' });
  }

  window.addEventListener('scroll', writeSavedScrollY, { passive: true });
  window.addEventListener('pagehide', writeSavedScrollY);
  window.addEventListener('pageshow', (event) => {
    if (!event.persisted) return;

    requestAnimationFrame(() => {
      requestAnimationFrame(restoreIfSafariMissedBFCacheScroll);
    });
  });
})();
